import React, { useState, useCallback, useEffect } from 'react';
import { UserProfile } from '../../../types/types';
import { RestUser } from '../../../services/auth/types/user';
import DashboardLayout from '../../layouts/DashboardLayout';
import ShareComposer from '../../components/feed/ShareComposer';
import FeedList from '../../components/feed/FeedList';
import { FeedPost } from '../../types/feed';
import {
  createPost as createPostApi,
  deletePost,
  getFeed,
  getMyPosts,
  presignPostMedia,
  MAX_POST_PHOTOS,
  ServerPost,
} from '../../../services/posts/api';
import { reencodeImage, uploadToPresignedPost } from '../../../services/media/upload';
import { getPublicProfile } from '../../../services/social/api';
import './FeedPage.css';

interface FeedPageProps {
  user: UserProfile;
  restUser: RestUser | null;
}

const recommendedTrips = [
  {
    title: 'Bali Adventure',
    meta: '7 days - $1,240',
    imageUrl: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?auto=format&fit=crop&w=400&q=80',
  },
  {
    title: 'Tokyo Lights',
    meta: '5 days - $980',
    imageUrl: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?auto=format&fit=crop&w=400&q=80',
  },
  {
    title: 'Paris Discovery',
    meta: '4 days - $860',
    imageUrl: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=400&q=80',
  },
  {
    title: 'Coastal Portugal',
    meta: '5 days - $1,120',
    imageUrl: 'https://images.unsplash.com/photo-1500375592092-40eb2168fd21?auto=format&fit=crop&w=400&q=80',
  },
  {
    title: 'Iceland Ring Road',
    meta: '9 days - $2,050',
    imageUrl: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=400&q=80',
  },
  {
    title: 'Morocco Colors',
    meta: '6 days - $930',
    imageUrl: 'https://images.unsplash.com/photo-1539020140153-e8c237112e53?auto=format&fit=crop&w=400&q=80',
  },
  {
    title: 'Alps Weekend',
    meta: '3 days - $640',
    imageUrl: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=400&q=80',
  },
  {
    title: 'Greek Islands',
    meta: '8 days - $1,780',
    imageUrl: 'https://images.unsplash.com/photo-1516483638261-f4dbaf036963?auto=format&fit=crop&w=400&q=80',
  },
  {
    title: 'Costa Rica Wild',
    meta: '7 days - $1,360',
    imageUrl: 'https://images.unsplash.com/photo-1518182170546-07661fd94144?auto=format&fit=crop&w=400&q=80',
  },
  {
    title: 'New York Food',
    meta: '4 days - $1,050',
    imageUrl: 'https://images.unsplash.com/photo-1485871981521-5b1fd3805eee?auto=format&fit=crop&w=400&q=80',
  },
];

const FeedPage: React.FC<FeedPageProps> = ({ user, restUser }) => {
  const [composerText, setComposerText] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [isLoadingFeed, setIsLoadingFeed] = useState(true);
  const [feedError, setFeedError] = useState<string | null>(null);
  const [isPosting, setIsPosting] = useState(false);

  const userAvatar = restUser?.picture_url || restUser?.avatarUrl || user?.avatar;
  const userName = restUser?.firstName || user?.name || 'You';
  const myUserId = restUser?.id || '';

  // T2.10a: real posts. The feed endpoint is followed-authors only, so the
  // page merges it with the caller's own recent posts (you don't follow
  // yourself). Author display names resolve via public profiles, cached per
  // author for the page's lifetime.
  useEffect(() => {
    let cancelled = false;

    const toFeedPost = (post: ServerPost, author: { name: string; avatarUrl?: string }): FeedPost => ({
      id: post.postId,
      author: { id: post.authorId, name: author.name, avatarUrl: author.avatarUrl },
      location: '',
      caption: post.text,
      // Expiring thumbnails from the media plane (live items only).
      imageUrls: (post.media || [])
        .filter((item) => item.status === 'live')
        .map((item) => item.thumbUrl || item.url)
        .filter((url): url is string => Boolean(url)),
      likes: 0,
      comments: post.commentCount ?? 0,
      createdAt: post.createdAt,
    });

    const load = async () => {
      setIsLoadingFeed(true);
      setFeedError(null);
      try {
        const [feedPage, minePage] = await Promise.all([
          getFeed(),
          getMyPosts().catch(() => ({ posts: [] as ServerPost[] })),
        ]);

        // buildFeed already fans in the caller's OWN posts (authors =
        // [userId, ...following]), so concatenating getMyPosts() rendered every
        // one of them twice — once with the resolved profile and once with the
        // 'Traveler' fallback. `mine` is kept only as a backfill for posts the
        // feed's per-author cap may have trimmed.
        const seen = new Set(feedPage.posts.map((post) => post.postId));
        const missingMine = minePage.posts.filter((post) => !seen.has(post.postId));

        const authorIds = Array.from(new Set(feedPage.posts.map((post) => post.authorId)))
          .filter((id) => id && id !== myUserId);
        const profiles = new Map<string, { name: string; avatarUrl?: string }>();
        await Promise.all(authorIds.map(async (id) => {
          const profile = await getPublicProfile(id).catch(() => null);
          profiles.set(id, {
            name: (profile as any)?.nickname || (profile as any)?.firstName || 'Traveler',
            avatarUrl: (profile as any)?.picture_url || (profile as any)?.avatarUrl || undefined,
          });
        }));

        const me = { name: userName, avatarUrl: userAvatar };
        const merged = [
          ...feedPage.posts.map((post) => toFeedPost(
            post,
            // The caller's own posts get their own name/avatar, not a lookup —
            // authorIds deliberately excludes myUserId.
            post.authorId === myUserId ? me : profiles.get(post.authorId) || { name: 'Traveler' },
          )),
          ...missingMine.map((post) => toFeedPost(post, me)),
        ].sort((a, b) => (a.id < b.id ? 1 : -1)); // time-sortable postIds → newest first

        if (!cancelled) setPosts(merged);
      } catch (err: any) {
        if (!cancelled) setFeedError(err?.message || 'Failed to load the feed.');
      } finally {
        if (!cancelled) setIsLoadingFeed(false);
      }
    };

    void load();
    return () => { cancelled = true; };
    // Reload only on identity change — composer prepends locally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myUserId]);

  const handleSubmit = useCallback(async () => {
    if (!composerText.trim() || isPosting) return;
    setIsPosting(true);
    try {
      const created = await createPostApi(composerText);

      // Photos (T2.10b): re-encode client-side (EXIF/GPS never leave the
      // device, HEIC→JPEG where the browser can decode), mint ALL upload slots
      // in ONE presign request, then upload straight to S3 in parallel. The
      // pipeline moderates + thumbnails async; local object URLs preview the
      // post instantly. Slots that fail to upload expire server-side via TTL.
      const files = attachments.slice(0, MAX_POST_PHOTOS);
      let localPreviews: string[] = [];
      if (files.length > 0) {
        try {
          const encoded = await Promise.all(files.map((file) => reencodeImage(file)));
          const targets = await presignPostMedia(created.postId, encoded.length);
          const uploaded = await Promise.all(encoded.map(async (item, index) => {
            const target = targets[index];
            if (!target) return null;
            try {
              await uploadToPresignedPost(target.upload, item.blob, item.contentType);
              return URL.createObjectURL(item.blob);
            } catch (uploadErr) {
              console.error('Photo upload failed', uploadErr);
              return null;
            }
          }));
          localPreviews = uploaded.filter((url): url is string => Boolean(url));
        } catch (photoErr) {
          console.error('Photo attach failed', photoErr);
        }
      }

      const newPost: FeedPost = {
        id: created.postId,
        author: { id: created.authorId, name: userName, avatarUrl: userAvatar },
        location: '',
        caption: created.text,
        imageUrls: localPreviews,
        likes: 0,
        comments: 0,
        createdAt: created.createdAt,
      };
      setPosts((prev) => [newPost, ...prev]);
      setComposerText('');
      setAttachments([]);
    } catch (err: any) {
      setFeedError(err?.message || 'Failed to publish the post.');
    } finally {
      setIsPosting(false);
    }
  }, [composerText, attachments, isPosting, userName, userAvatar]);

  const handleLike = useCallback((postId: string) => {
    setPosts((prev) =>
      prev.map((post) => {
        if (post.id !== postId) return post;
        const isLiked = !post.isLiked;
        return {
          ...post,
          isLiked,
          likes: isLiked ? post.likes + 1 : Math.max(0, post.likes - 1),
        };
      })
    );
  }, []);

  const handleEditPost = useCallback((_postId: string) => {
    setFeedError('Post editing is coming soon.');
  }, []);

  const handleDeletePost = useCallback(async (postId: string) => {
    const previous = posts;
    setPosts((current) => current.filter((post) => post.id !== postId));
    setFeedError(null);
    try {
      await deletePost(postId);
    } catch (err: any) {
      setPosts(previous);
      setFeedError(err?.message || 'Failed to delete the post.');
    }
  }, [posts]);

  return (
    <DashboardLayout activeSection="feed">
      <div className="v2-feed-page">
        <header className="v2-feed-page__header">
          <h1 className="v2-feed-page__title">Good morning, {userName}</h1>
        </header>

        <section className="v2-feed-page__recommendations" aria-labelledby="recommendations-heading">
          <div className="v2-feed-page__section-header">
            <h2 id="recommendations-heading">Recommended for you</h2>
            <button type="button">See all</button>
          </div>
          <div className="v2-feed-page__recommendation-list">
            {recommendedTrips.map((trip) => (
              <article key={trip.title} className="v2-feed-page__recommendation-card">
                <img src={trip.imageUrl} alt="" />
                <div>
                  <h3>{trip.title}</h3>
                  <p>{trip.meta}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <ShareComposer
          userAvatar={userAvatar}
          userName={userName}
          value={composerText}
          onChange={setComposerText}
          onSubmit={handleSubmit}
          disabled={isPosting}
          attachments={attachments}
          onAttachmentsChange={setAttachments}
          maxAttachments={MAX_POST_PHOTOS}
        />

        <section className="v2-feed-page__feed" aria-labelledby="feed-heading">
          <h2 id="feed-heading" className="v2-feed-page__section-title">Travel Feed</h2>
          {feedError && <p className="v2-feed-page__notice">{feedError}</p>}
          {isLoadingFeed && <p className="v2-feed-page__notice">Loading your feed…</p>}
          {!isLoadingFeed && !feedError && posts.length === 0 && (
            <p className="v2-feed-page__notice">
              Nothing here yet — share your first post above, or follow travelers to see theirs.
            </p>
          )}
          <FeedList
            posts={posts}
            currentUserId={myUserId}
            onLike={handleLike}
            onComment={(id) => console.log('Comment on', id)}
            onShare={(id) => console.log('Share', id)}
            onEdit={handleEditPost}
            onDelete={handleDeletePost}
          />
        </section>
      </div>
    </DashboardLayout>
  );
};

export default FeedPage;
